import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

export interface CategoryInfo {
	key: string;
	icon?: IconName;
	title?: string;
	subtitle?: string;
	baseColor?: string;
	style?: SafeString;
}

export default class BoardPopupSelectCategoryComponent extends PopupInnerModalComponent<PopupData.Board.SelectCategory> {
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('app-notifications') declare appNotificationService: Service.AppNotifications;

	get connectSelectionsSourceTypes() {
		return this.appNotificationService.connectSelections;
	}

	get formattedCategories(): CategoryInfo[] {
		// Get categories from connected and selected sources
		const categoriesFromConnectedSources = this.mmSourcesService.sourceModels
			.map((source) => source.sourceType?.categoryArray)
			.flat()
			.sort(() => Math.random() - 0.5); // Randomize array

		// Get categories from selected sources
		const categoriesFromSelectedSources = this.connectSelectionsSourceTypes
			.map((source) => source.sourceType?.categoryArray || [])
			.flat();

		// Combine and deduplicate connected and selected categories
		const primaryCategories = [
			...this.formatCategory(categoriesFromConnectedSources),
			...this.formatCategory(categoriesFromSelectedSources),
		].filter((currentItem, currentIndex, array) => {
			const firstIndex = array.findIndex((item) => item.title === currentItem.title);
			return currentIndex === firstIndex;
		});

		// If we have 10 or more primary categories, return just those
		if (primaryCategories.length >= 9) {
			return primaryCategories;
		}

		// Get all titles from primary categories for easier comparison, title is unique
		const primaryTitles = new Set(primaryCategories.map((cat) => cat.title));

		// Get popular categories, excluding any that exist in primaryCategories
		const categoriesFromPopularSources = this.mmSourceTypesService.sourceTypeModels
			.sort((a, b) => (a.popularity || Infinity) - (b.popularity || Infinity))
			.map((sourceType) => sourceType.categoryArray)
			.flat()
			.filter((category, index, self) => self.indexOf(category) === index); // Remove duplicates

		// Format popular categories, excluding any that exist in primaryCategories
		const remainingPopularCategories = this.formatCategory(categoriesFromPopularSources)
			.filter((category) => !primaryTitles.has(category.title))
			.slice(0, 9 - primaryCategories.length);

		return [...primaryCategories, ...remainingPopularCategories];
	}

	@action
	selectCategory(category: CategoryInfo) {
		this.popupData.onSelect(category);
		this.close();
	}

	@action
	selectSurpriseMe() {
		// will be reworked into proper category
		this.selectCategory({
			key: 'surprise_me',
		});
	}

	private allCategoriesInfo = [
		{
			icon: 'Money',
			title: 'Financials',
			subtitle: 'Expenses, Income, Profit, Loss, Cash Flow',
			key: 'accounting_and_invoicing',
			baseColor: '29, 154, 214',
			relatedCategories: ['Accounting & Invoicing'],
		},
		{
			icon: 'Timezone',
			title: 'Website analytics',
			subtitle: 'Views, Sessions, Users, Events, Conversions',
			key: 'analytics',
			baseColor: '29, 154, 214',
			relatedCategories: ['Analytics'],
		},
		{
			icon: 'Investment',
			title: 'Sales performance & deals (CRM)',
			subtitle: 'Deals, Deal Amount, Opportunities, Activities',
			key: 'crm',
			baseColor: '29, 154, 214',
			relatedCategories: ['CRM'],
		},
		{
			icon: 'Phone',
			title: 'Support activities',
			subtitle: 'Tickets, Conversations, Resolution Time, Calls',
			key: 'customer_support',
			baseColor: '88, 108, 172',
			relatedCategories: ['Customer Support'],
		},
		{
			icon: 'Cart',
			title: 'Ecommerce store performance',
			subtitle: 'Sales, Orders, Customers, Products, AOV',
			key: 'ecommerce',
			baseColor: '145, 102, 188',
			relatedCategories: ['Ecommerce'],
		},
		{
			icon: 'Mail',
			title: 'Email marketing',
			subtitle: 'Clicks, Opens, CTR, Subscribers, Campaigns',
			key: 'email_marketing',
			baseColor: '145, 102, 188',
			relatedCategories: ['Email Marketing'],
		},
		{
			icon: 'Chat',
			title: 'Help docs insights',
			subtitle: 'Visits, Search Results, Articles, Search Terms',
			key: 'help_desk',
			baseColor: '145, 102, 188',
			relatedCategories: ['Help Desk'],
		},
		{
			icon: 'Megaphone',
			title: 'Email marketing automation',
			subtitle: 'Contacts, Emails, Leads, Clicks, Opens',
			key: 'marketing_automation',
			baseColor: '145, 102, 188',
			relatedCategories: ['Marketing Automation'],
		},
		{
			icon: 'CreditCard',
			title: 'Paid advertising',
			subtitle: 'Spent, CPC, CTR, Impressions, Campaigns',
			key: 'paid_advertising',
			baseColor: '250, 135, 12',
			relatedCategories: ['Paid Advertising'],
		},
		{
			icon: 'ListCheck',
			title: 'Project management',
			subtitle: 'Issues, Hours Tracked, Billable Amount',
			key: 'project_management',
			baseColor: '250, 135, 12',
			relatedCategories: ['Project Management'],
		},
		{
			icon: 'Search',
			title: 'Search analytics (SEO)',
			subtitle: 'Position, Keywords, Clicks, Rank, Backlinks',
			key: 'seo',
			baseColor: '250, 135, 12',
			relatedCategories: ['SEO'],
		},
		{
			icon: 'CommentLike',
			title: 'Social media engagement',
			subtitle: 'Likes, Posts, Reach, Followers, Impressions',
			key: 'social_media',
			baseColor: '250, 135, 12',
			relatedCategories: ['Social Media'],
		},
		{
			icon: 'CloudUpload',
			title: 'Subscriptions and revenue',
			subtitle: 'Customers, MRR, Churn, Subscriptions, Balance',
			key: 'subscriptions_and_payments',
			baseColor: '37, 167, 103',
			relatedCategories: ['Subscriptions and Payments'],
		},
		{
			icon: 'Camera',
			title: 'Video',
			subtitle: 'Views, Likes, Watch Time, Plays, Subscribers',
			key: 'video',
			baseColor: '37, 167, 103',
			relatedCategories: ['Video', 'Video Marketing'],
		},
	];

	private formatCategory(sortedCategories: string[]) {
		// Create a map of all possible CategoryInfo objects indexed by their related categories
		const categoryInfoMap = this.allCategoriesInfo.reduce(
			(acc, categoryInfo) => {
				categoryInfo.relatedCategories.forEach((relatedCategory) => {
					acc[relatedCategory] = categoryInfo;
				});
				return acc;
			},
			{} as Record<string, (typeof this.allCategoriesInfo)[0]>,
		);

		// Map sorted categories to CategoryInfo objects while maintaining order
		return sortedCategories
			.map((category) => {
				const matchingCategoryInfo = categoryInfoMap[category];
				if (!matchingCategoryInfo) return null;

				return {
					...matchingCategoryInfo,
					style: htmlSafe(
						`color: rgb(${matchingCategoryInfo.baseColor}); background-color: rgba(${matchingCategoryInfo.baseColor},0.1);`,
					),
				} as CategoryInfo;
			})
			.filter((item): item is CategoryInfo => item !== null);
	}
	<template>
		<div class="title title--vertical">
			<img src="/images/svg/wand-gradient-purple.svg" height="48" width="48" alt="Header" />
			<span class="text">What kind of dashboard would you like to create?</span>
		</div>
		<div class="content">

			<div class="category-list">
				{{#each this.formattedCategories as |category|}}
					<div
						role="button"
						class="category-list__item"
						{{on "click" (fn this.selectCategory category)}}
					>
						{{#if category.icon}}
							<span class="category-list__item-icon" style={{category.style}}>
								<CommonUiIconComponent @name={{category.icon}} @size="sm" />
							</span>
						{{/if}}
						<div class="category-list__item-title-wrapper">
							<div class="category-list__item-title">{{category.title}}</div>
							{{#if category.subtitle}}
								<div class="category-list__item-subtitle">
									{{category.subtitle}}...
								</div>
							{{/if}}
						</div>
						<img
							class="category-list__item-sparkles"
							src="/images/svg/sparkles-gradient-purple.svg"
							alt="Sparkles"
						/>
					</div>
				{{/each}}
				<div
					role="button"
					class="category-list__item category-list__item--surprise-me"
					{{on "click" this.selectSurpriseMe}}
				>
					<span class="category-list__item-icon">
						<CommonUiIconComponent @name="MoodSmile" @size="sm" />
					</span>
					<div class="category-list__item-title-wrapper">
						<div class="category-list__item-title">Surprise me</div>
					</div>
					<img
						class="category-list__item-sparkles"
						src="/images/svg/sparkles-gradient-purple.svg"
						alt="Sparkles"
					/>
				</div>
			</div>

		</div>
	</template>
}
